import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card'
import { useAuth } from '@/features/auth/AuthContext'

const schema = z.object({
  email: z.string().email('Neplatný e-mail'),
  password: z.string().min(1, 'Vyplňte heslo'),
})

type FormValues = z.infer<typeof schema>

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(values: FormValues) {
    setSubmitError(null)
    try {
      await login(values.email, values.password)
      navigate(from, { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setSubmitError(typeof msg === 'string' ? msg : 'Neplatné přihlašovací údaje.')
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <img src="/star-logo.png" alt="STAR INSURANCE" className="h-10 w-auto" />
          <span className="text-sm font-medium text-zinc-500">VINport</span>
        </div>
      </header>
      <div className="bg-gradient-to-r from-purple-500 to-orange-400 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-white">Přihlášení</h1>
          <p className="mt-1 text-sm text-white/90">Přihlaste se a začněte pracovat s vozidly.</p>
        </div>
      </div>
      <div className="flex flex-1 items-start justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Přihlášení</CardTitle>
          <CardDescription>Zadejte své přihlašovací údaje.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" autoComplete="email" {...register('email')} />
              {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Heslo</Label>
              <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
              {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
            </div>
            {submitError && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? 'Přihlašuji…' : 'Přihlásit se'}
            </Button>
            <p className="text-sm text-zinc-500">
              Nemáte účet?{' '}
              <Link to="/register" className="text-brand hover:underline">Zaregistrujte se</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
      </div>
    </div>
  )
}
